/**
 * `/r/:code`: a private room (docs/design/05 §8): the host picks the tier (shown to the guest before
 * joining), both pick a deck, the host starts the match. The room is polled (rooms have no socket);
 * once a match exists both players are sent to it.
 */
import type { RoomView } from '@play/protocol';
import {
  Field,
  Frame,
  GhostButton,
  LOOK,
  Notice,
  PageHead,
  PrimaryButton,
  StatusBadge,
  WELL,
  cn,
  inputClass,
  inputStyle,
} from '@play/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  Check,
  Copy,
  Crown,
  Layers,
  Link2,
  Loader2,
  LogOut,
  Play as PlayIcon,
  UserRound,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/auth';
import { SignInGate } from '../auth/SignInPanel';
import { DeckTile } from '../features/play/DeckTile';
import { keepDeckSelection, readLastDeck, useDeckCheck, useDecksLegality, useMyDecks } from '../features/play/decks';
import { TIER_INFO } from '../features/play/TierPicker';
import { api, failureMessage } from '../lib/api';
import { bindMatchDeck, rememberPendingDeck } from '../lib/deck-reference';

const roomApi = getRouteApi('/shell/r/$code');

function Seat({ label, info, ready }: { label: string; info: RoomView['host'] | null; ready: boolean }) {
  return (
    <div
      className="flex items-center gap-3 rounded-[10px] px-3 h-[56px]"
      style={{ background: WELL, boxShadow: `inset 0 0 0 1px ${LOOK.border}` }}
    >
      <span
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={
          label === 'Host'
            ? { background: 'rgba(217,164,65,0.14)', color: LOOK.gold }
            : { background: LOOK.raised, color: LOOK.sub }
        }
      >
        {label === 'Host' ? <Crown size={15} /> : <UserRound size={15} />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[11.5px]" style={{ color: LOOK.muted }}>
          {label}
        </span>
        <span className="block text-[13.5px] font-semibold truncate" style={{ color: info ? LOOK.text : LOOK.faint }}>
          {info ? (info.displayName ?? info.handle ?? 'Player') : 'Waiting for a player…'}
        </span>
      </span>
      {info ? (
        <StatusBadge color={ready ? LOOK.ok : LOOK.muted}>{ready ? 'Deck ready' : 'Choosing a deck'}</StatusBadge>
      ) : null}
    </div>
  );
}

/** The room page. */
export function RoomPage() {
  const { code } = roomApi.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState<RoomView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deckId, setDeckId] = useState<string | null>(readLastDeck);
  const decks = useMyDecks(user !== null);
  const check = useDeckCheck(deckId);
  const [explicit, setExplicit] = useState(false);
  const legality = useDecksLegality(decks.data, room?.tier ?? 1);
  const autoNext = keepDeckSelection(decks.data, deckId, { explicit, legal: legality });
  useEffect(() => {
    if (autoNext !== deckId) setDeckId(autoNext);
  }, [autoNext, deckId]);

  const userId = user?.id ?? null;
  // The table's own deck list (lib/deck-reference.ts): the deck you seat with is held for this room, then
  // snapshotted for the match id the room starts.
  const holdDeck = () => {
    if (userId && check.data) rememberPendingDeck(userId, { kind: 'room', code }, check.data.deck);
  };
  const toMatch = useCallback(
    (matchId: string) => {
      if (userId) bindMatchDeck(userId, { kind: 'room', code }, matchId);
      void navigate({ to: '/match/$id', params: { id: matchId } });
    },
    [code, navigate, userId],
  );
  const refresh = useCallback(async () => {
    try {
      const r = await api.room(code);
      setRoom(r);
      if (r.matchId && (r.status === 'started' || r.status === 'ready')) toMatch(r.matchId);
    } catch (e) {
      setError(failureMessage(e));
    }
  }, [code, toMatch]);

  useEffect(() => {
    if (!user) return;
    void refresh();
    const t = setInterval(() => void refresh(), 2000);
    return () => {
      clearInterval(t);
    };
  }, [user, refresh]);

  if (loading) return null;
  if (!user)
    return (
      <SignInGate title="Join this room" next={`/r/${code}`}>
        Sign in with your Runebound account to pick a deck and take the seat.
      </SignInGate>
    );
  if (!room) {
    return error ? (
      <div className="mx-auto max-w-[480px] pt-14">
        <Frame title="Room unavailable" icon={<AlertCircle size={15} color={LOOK.bad} />}>
          <div className="p-4 flex flex-col gap-4 items-start">
            <p className="text-[13px]" style={{ color: LOOK.sub }}>
              {error}
            </p>
            <GhostButton
              onClick={() => {
                void navigate({ to: '/play' });
              }}
            >
              Back to Play
            </GhostButton>
          </div>
        </Frame>
      </div>
    ) : (
      <div className="flex justify-center py-24" style={{ color: LOOK.muted }}>
        <Loader2 className="animate-spin motion-reduce:animate-none" />
      </div>
    );
  }
  const isHost = room.host.userId === user.id;
  const isGuest = room.guest?.userId === user.id;
  const tierInfo = room.tier === 3 ? { name: 'Rules engine', blurb: '' } : TIER_INFO[room.tier];
  const deckOk = deckId !== null && check.data?.tiers[room.tier] === true;
  const act = async (fn: () => Promise<RoomView | undefined>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (r) setRoom(r);
    } catch (e) {
      setError(failureMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col">
      <PageHead
        title="Private room"
        icon={<Link2 size={18} />}
        badge={<StatusBadge color={LOOK.violet}>Tier {room.tier}</StatusBadge>}
      >
        {tierInfo.name} · Standard · 1v1
      </PageHead>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start">
        <Frame
          title="Choose your deck"
          icon={<Layers size={15} color="#b9adff" />}
          end={
            <span className="text-[12.5px] hidden sm:inline" style={{ color: LOOK.muted }}>
              {isHost || isGuest ? 'Your opponent never sees your list.' : 'Join with one of your decks.'}
            </span>
          }
        >
          <div className="p-4">
            {decks.data && decks.data.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-2.5">
                {decks.data.map((d) => (
                  <DeckTile
                    key={d.id}
                    deck={d}
                    selected={d.id === deckId}
                    onSelect={() => {
                      setExplicit(true);
                      setDeckId(d.id);
                    }}
                  />
                ))}
              </div>
            ) : (
              <p className="text-[13px] py-4" style={{ color: LOOK.muted }}>
                {decks.isPending ? 'Loading decks…' : 'No decks yet. Build one on runebound.gg.'}
              </p>
            )}
          </div>
        </Frame>
        <div className="lg:sticky lg:top-[81px]">
          <Frame
            title={`Room ${room.code}`}
            icon={<Link2 size={15} color="#b9adff" />}
            end={
              <StatusBadge color={room.status === 'open' ? LOOK.ok : LOOK.muted}>
                {room.status === 'open' ? 'Open' : room.status}
              </StatusBadge>
            }
          >
            <div className="p-4 flex flex-col gap-3">
              <Field label={room.status === 'open' ? 'Share the link with your opponent' : 'Room link'}>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={room.url}
                    className={cn(inputClass, 'flex-1 min-w-0 mono text-[12px]')}
                    style={inputStyle}
                    aria-label="Room link"
                    onFocus={(e) => {
                      e.currentTarget.select();
                    }}
                  />
                  <GhostButton
                    onClick={() => {
                      void navigator.clipboard.writeText(room.url).then(() => {
                        setCopied(true);
                      });
                    }}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied' : 'Copy'}
                  </GhostButton>
                </div>
              </Field>
              <Seat label="Host" info={room.host} ready={room.hostReady} />
              <Seat label="Guest" info={room.guest} ready={room.guestReady} />
              {!isHost && !isGuest && room.guest === null ? (
                <PrimaryButton
                  big
                  className="w-full"
                  disabled={!deckOk || busy}
                  onClick={() =>
                    void act(async () => {
                      const r = await api.joinRoom(code, deckId ?? '');
                      holdDeck();
                      return r;
                    })
                  }
                >
                  Join with this deck
                </PrimaryButton>
              ) : null}
              {(isHost || isGuest) && deckId ? (
                <GhostButton
                  className="w-full !h-10"
                  disabled={!deckOk || busy}
                  onClick={() =>
                    void act(async () => {
                      const r = await api.roomDeck(code, deckId);
                      holdDeck();
                      return r;
                    })
                  }
                >
                  Use this deck
                </GhostButton>
              ) : null}
              {isHost ? (
                <PrimaryButton
                  big
                  className="w-full"
                  disabled={!room.hostReady || !room.guestReady || busy}
                  onClick={() =>
                    void act(async () => {
                      const m = await api.startRoom(code);
                      toMatch(m.matchId);
                      return undefined;
                    })
                  }
                >
                  <PlayIcon size={16} fill="currentColor" /> Start the match
                </PrimaryButton>
              ) : null}
              {isHost || isGuest ? (
                <button
                  type="button"
                  className="h-9 rounded-[9px] inline-flex items-center justify-center gap-2 text-[13px] font-medium transition-colors hover:bg-white/[0.05]"
                  style={{ color: LOOK.sub }}
                  onClick={() =>
                    void act(async () => {
                      await api.leaveRoom(code);
                      void navigate({ to: '/play' });
                      return undefined;
                    })
                  }
                >
                  <LogOut size={14} /> Leave room
                </button>
              ) : null}
              {error ? (
                <Notice tone="bad" role="alert">
                  {error}
                </Notice>
              ) : null}
            </div>
          </Frame>
        </div>
      </div>
    </div>
  );
}
